/**
 * heatmapStyle — pure computation helpers extracted from HeatmapTableCard so
 * the Meridian heatmap conversion can share the exact same math. These are
 * verbatim moves of the inline logic that previously lived in
 * HeatmapTableCard.jsx; the numbers must not change. Cell colors come from
 * `heatScale.js` (`heatCell`), not from parsed style strings.
 */

/**
 * Compute the footer-row values for a heatmap table. Returns the per-quarter
 * sums (averaged over active rows when summaryType==='average'), the total, the
 * forecast total/delta, the pacing total/delta, and the showPace flag.
 *
 * @param {Array<{q1:number,q2:number,q3:number,q4:number,total:number,hour:string,actualAvailable?:boolean}>} rows
 * @param {string} summaryType - 'total' | 'average'
 * @param {Object|null} forecast - map of hour -> forecast value, or null
 * @returns {{
 *   label:string, isAvg:boolean, divisor:number,
 *   sq1:number, sq2:number, sq3:number, sq4:number, st:number,
 *   hasForecast:boolean, fcstTotal:number|null, fcstDelta:number|null,
 *   fcstPace:number|null, fcstPaceDelta:number|null, showPace:boolean,
 *   actualRowsLength:number, rowsLength:number
 * }}
 */
export function computeFooter(rows, summaryType, forecast) {
    const isAvg = summaryType === 'average';
    const label = isAvg ? 'Avg' : 'Total';
    const active = rows.filter(r => r.q1 || r.q2 || r.q3 || r.q4);
    const divisor = isAvg ? (active.length || 1) : 1;

    const sumQ1 = rows.reduce((s, r) => s + r.q1, 0);
    const sumQ2 = rows.reduce((s, r) => s + r.q2, 0);
    const sumQ3 = rows.reduce((s, r) => s + r.q3, 0);
    const sumQ4 = rows.reduce((s, r) => s + r.q4, 0);
    const sumTotal = rows.reduce((s, r) => s + r.total, 0);
    const v = n => isAvg ? Math.round(n / divisor) : n;

    // Forecast footer totals
    const hasForecast = forecast != null;
    let fcstTotal = null;
    let fcstDelta = null;
    // "So far" pacing uses rows whose actual data was available when the
    // performance fetch completed. Fall back to positive totals for legacy rows
    // that predate the explicit availability flag.
    const actualRows = rows.filter(r => r.actualAvailable ?? (r.total > 0));
    let fcstPace = null;
    let fcstPaceDelta = null;
    if (hasForecast) {
        fcstTotal = rows.reduce((sum, r) => sum + (forecast[r.hour] || 0), 0);
        fcstDelta = v(sumTotal) - Math.round(fcstTotal);

        if (actualRows.length > 0 && actualRows.length < rows.length) {
            // Shift is in progress — compute pacing delta through the last
            // available actual hour, including observed zero-move hours.
            const actualSoFar = actualRows.reduce((sum, r) => sum + r.total, 0);
            const fcstSoFar = actualRows.reduce((sum, r) => sum + (forecast[r.hour] || 0), 0);
            fcstPace = Math.round(fcstSoFar);
            fcstPaceDelta = actualSoFar - fcstPace;
        }
    }

    // Show pace delta inline in the Delta cell when shift is in progress
    const showPace = hasForecast && fcstPaceDelta != null && fcstDelta !== fcstPaceDelta;

    return {
        label,
        isAvg,
        divisor,
        sq1: v(sumQ1),
        sq2: v(sumQ2),
        sq3: v(sumQ3),
        sq4: v(sumQ4),
        st: v(sumTotal),
        hasForecast,
        fcstTotal,
        fcstDelta,
        fcstPace,
        fcstPaceDelta,
        showPace,
        actualRowsLength: actualRows.length,
        rowsLength: rows.length,
    };
}
